import React, { useEffect, useState } from "react";
import { Button, Form, Image, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";
import CommonModal from "components/CommonModal";
import TextInput from "components/TextInput";
import { getProductList, createSupportList } from "api/ActivityService";
import { toast } from "react-toastify";

const schema = yup.object().shape({
  name: yup.string().trim().min(1, "List Name is required"),
  products: yup
    .array()
    .min(1, "Please select at least one product")
    .of(
      yup.object().shape({
        value: yup.string().required(),
        label: yup.string().required(),
      })
    ),
});

const CreateListModal = ({ show, onClose, onListCreated }) => {
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [is_disabled, setIsDisabled] = useState(false);
  const {
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      products: [],
    },
  });
  const selectedProducts = watch("products");
  useEffect(() => {
    if (show) {
      fetchProducts();
    }
  }, [show]);
  const Option = ({ innerProps, label, data }) => (
    <div {...innerProps} className="d-flex align-items-center p-2">
      <Image src={data.logoUrl} width={30} height={30} className="me-2" />
      <span>{label}</span>
    </div>
  );
  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const res = await getProductList();
      if (res.success) {
        const options = res.body.map((product) => ({
          label: product.name,
          value: product._id,
          logoUrl: product.logoUrl,
        }));
        setProductOptions(options);
        setLoadingProducts(false);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const onSubmit = async (data) => {
    setIsDisabled(true);
    try {
      const payload = {
        name: data.name.trim(),
        productId: data.products.map((p) => p.value),
      };

      const res = await createSupportList(payload);
      if (res.success) {
        toast.success(res.message);
        setIsDisabled(false);
        onClose();
        reset();
        onListCreated?.();
      } else {
        toast.error(res.message || "Something went wrong");
        setIsDisabled(false);
      }
    } catch (err) {
      setIsDisabled(false);
      console.error("Create list error:", err);
      toast.error("Failed to create list");
    }
  };

  return (
    <CommonModal
      title={"Create New List"}
      para={
        "Create a new list to save and organize the best software products tailored to your needs."
      }
      show={show}
      onClose={() => {
        reset();
        onClose();
      }}
      body={
        <Form onSubmit={handleSubmit(onSubmit)} className="mt-4">
          <TextInput
            labelText={"List Name"}
            placeholder={"Enter List Name"}
            {...register("name")}
            errorMessage={errors.name?.message}
          />

          <Form.Group className="mt-3">
            <Form.Label className="d-block text-start">
              Select Products
            </Form.Label>

            <Select
              isMulti
              styles={{
                control: (styles, state) => ({
                  ...styles,
                  borderRadius: "100px !important",
                  // borderRadius: 100, // No need for !important
                  flexWrap: "wrap", // ✅ allow tags to wrap
                  boxShadow: state.isFocused ? styles.boxShadow : "none", // optional cleanup
                  borderColor: state.isFocused
                    ? styles.borderColor
                    : styles.borderColor,
                  "&:focus": {
                    borderRadius: "100px !important",
                  },
                }),
                multiValue: (styles) => ({
                  ...styles,
                  borderRadius: 100,
                  padding: "2px 8px", // ✅ smaller padding
                  margin: "10px", // ✅ smaller margin
                  maxWidth: "100%", // ensure the multi-value container takes full width
                }),
              }}
              options={productOptions}
              value={selectedProducts}
              onChange={(selected) => setValue("products", selected)}
              components={{ Option }}
              isLoading={loadingProducts}
              closeMenuOnSelect={false}
              className={`react-select-container ${
                errors.products ? "is-invalid" : ""
              }`}
              classNamePrefix="react-select"
            />

            {errors.products && (
              <span className="text-danger mt-1 text-start d-block">
                {errors.products.message}
              </span>
            )}
          </Form.Group>

          <Button
            type="submit"
            variant="info"
            disabled={is_disabled}
            className="w-100 mt-4 border-radius-100"
          >
            {is_disabled ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Continue"
            )}
          </Button>
        </Form>
      }
    />
  );
};

export default CreateListModal;
